/**
 * iter139 — RSVP FOMO nudge (haptic + micro-tap sound).
 *
 * Fired when another member starts *considering* the same session slot. The
 * design intent is a tiny "someone's eyeing the seat you're eyeing" tap —
 * never a full notification. Both signals are best-effort and silently no-op
 * on unsupported devices.
 *
 *   • haptic: navigator.vibrate([8]) — sub-perceptual on Android, ignored on iOS.
 *   • sound:  Web Audio API tiny 60ms 880Hz sine blip at 0.08 gain (≈ a UI
 *             "tick"). Lazily creates a single AudioContext that auto-resumes
 *             after the first user gesture (browser autoplay policy).
 *
 * Usage:
 *   import { playRsvpNudge, primeAudio } from '../lib/rsvpNudge';
 *   <Button onPointerDown={primeAudio} ...
 *   playRsvpNudge();
 */

let audioCtx = null;
let primed = false;

function ensureCtx() {
  if (audioCtx) return audioCtx;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    audioCtx = new Ctx();
  } catch {
    audioCtx = null;
  }
  return audioCtx;
}

/** Call once on any user gesture (button mount / first hover) to unlock audio
 *  under browser autoplay policies. Safe to call repeatedly. */
export function primeAudio() {
  if (primed) return;
  const ctx = ensureCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
  primed = true;
}

function tick(frequency = 880, durationMs = 60, gain = 0.08) {
  const ctx = ensureCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    // Browser hasn't seen a gesture yet — skip silently. Vibrate still works.
    return;
  }
  try {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = frequency;
    // ASR envelope so it doesn't click ugly.
    const now = ctx.currentTime;
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(gain, now + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, now + durationMs / 1000);
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + durationMs / 1000 + 0.02);
  } catch {
    /* ignore */
  }
}

/** Fire the micro-FOMO tap. Optionally pass `{ intense: true }` to bump it. */
export function playRsvpNudge({ intense = false } = {}) {
  // Respect users who prefer reduced motion — they almost certainly want
  // reduced audio cues too.
  try {
    if (window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
  } catch { /* ignore */ }

  try {
    if (navigator.vibrate) {
      navigator.vibrate(intense ? [12, 30, 12] : [8]);
    }
  } catch { /* ignore */ }

  tick(intense ? 1020 : 880, intense ? 90 : 55, intense ? 0.1 : 0.07);
}
